import { Component, Input } from '@angular/core';
import { LoButton, LoNotice } from 'lo-design-system';
import { NeedsPageAccessAccount } from '@brand-lolly/app/_models/brand-instagram-integration.model';

const BUSINESS_SETTINGS_URL = 'https://business.facebook.com/settings/pages';

const FIX_SENTENCE =
	'ask a business admin to give you a task on its Facebook Page, then reconnect and select it in Meta’s dialog.';

const POINTER_SENTENCE =
	'An Instagram account shows up here only through a Facebook Page you hold a task on. Check the Page’s people in Business settings and reconnect Facebook, then select that Page and its Instagram account in Meta’s dialog.';

/**
 * What to do about Instagram accounts that could not be connected: the ones
 * discovery saw through the business but not through a Page the user holds a
 * task on, one line each, and — for an empty list of accounts — where to look
 * for the ones Meta did not show at all.
 */
@Component({
	standalone: true,
	selector: 'meta-needs-page-access',
	imports: [LoButton, LoNotice],
	template: `
		@if (accountNames || showPointer) {
		<lo-notice tone="warning" [heading]="title" [body]="body">
			@if (showPointer) {
			<a
				lo-button
				variant="secondary"
				size="sm"
				target="_blank"
				rel="noopener"
				[href]="businessSettingsUrl"
			>
				Business settings
			</a>
			}
		</lo-notice>
		}
	`,
	styles: [
		`
			:host {
				display: block;
			}
		`
	]
})
export class MetaNeedsPageAccessComponent {
	accountNames = '';
	body = '';
	title = 'Missing an Instagram account?';
	businessSettingsUrl = BUSINESS_SETTINGS_URL;

	private _showPointer = false;

	/** Also explain where to look for accounts Meta did not show at all. */
	@Input() set showPointer(value: boolean) {
		this._showPointer = value;
		this.refreshBody();
	}
	get showPointer(): boolean {
		return this._showPointer;
	}

	@Input() set accounts(value: NeedsPageAccessAccount[] | null | undefined) {
		this.accountNames = (value ?? [])
			.map(account =>
				account.business_name
					? `@${account.ig_username} (${account.business_name})`
					: `@${account.ig_username}`
			)
			.join(', ');
		this.title = this.accountNames
			? 'Instagram accounts that need Page access'
			: 'Missing an Instagram account?';
		this.refreshBody();
	}

	private refreshBody(): void {
		const parts: string[] = [];
		if (this.accountNames) {
			parts.push(`${this.accountNames} — ${FIX_SENTENCE}`);
		}
		if (this._showPointer) {
			parts.push(POINTER_SENTENCE);
		}
		this.body = parts.join(' ');
	}
}
